import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { Megaphone } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { CampaignFilterPill } from '@/components/CampaignFilterPill';
import { NewLeadModal } from '@/components/NewLeadModal';

interface Lead {
  id: string;
  campaignId: string | null;
  // Joined by GET /leads. Optional so a client pointed at an older API still
  // type-checks; the row then renders without the campaign chip.
  campaignName?: string | null;
  source: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  companyName: string | null;
  status: string;
  qualificationNote: string | null;
}

interface CsvImportResult {
  created: number;
  duplicates: number;
  errors: number;
}

const LEAD_FIELDS = [
  { value: '', label: '-- Skip --' },
  { value: 'firstName', label: 'First Name' },
  { value: 'lastName', label: 'Last Name' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'companyName', label: 'Company Name' },
] as const;

/** Heuristic auto-match of common CSV column names to lead fields. */
function guessField(header: string): string {
  const h = header.toLowerCase().replace(/[^a-z]/g, '');
  if (h.includes('firstname') || h === 'first' || h === 'nombre') return 'firstName';
  if (h.includes('lastname') || h === 'last' || h === 'apellido') return 'lastName';
  if (h.includes('email') || h.includes('correo')) return 'email';
  if (h.includes('phone') || h.includes('telefono') || h.includes('tel')) return 'phone';
  if (h.includes('company') || h.includes('empresa') || h.includes('companyname'))
    return 'companyName';
  return '';
}

function parseCsvHeaders(text: string): string[] {
  const firstLine = text.split('\n')[0] ?? '';
  return firstLine.split(',').map((h) => h.trim().replace(/^"|"$/g, ''));
}

/**
 * Story 4.15 — lead queue with qualify/discard actions. v1 ships a
 * vertical list with status filter tabs and inline action buttons;
 * richer filtering and column ordering are follow-up work once
 * real rep usage tells us what to optimise for.
 *
 * Story 36.1 — CSV import modal with column mapping, added inline.
 */
export function LeadsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const campaignId = searchParams.get('campaignId');
  const [leads, setLeads] = useState<Lead[]>([]);
  const [filter, setFilter] = useState<'NEW' | 'QUALIFIED' | 'DISCARDED' | 'ALL'>('NEW');
  const [refreshToken, setRefreshToken] = useState(0);

  // Manual create state
  const [newLeadOpen, setNewLeadOpen] = useState(false);

  // CSV import state
  const [importOpen, setImportOpen] = useState(false);
  const [importStep, setImportStep] = useState<'file' | 'mapping' | 'done'>('file');
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [columnMapping, setColumnMapping] = useState<Record<string, string>>({});
  const [importResult, setImportResult] = useState<CsvImportResult | null>(null);
  const [importErrorDetail, setImportErrorDetail] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const params: Record<string, string> = {};
    if (filter !== 'ALL') params.status = filter;
    if (campaignId) params.campaignId = campaignId;
    apiClient
      .get<Lead[]>('/leads', { params })
      .then(({ data }) => setLeads(data))
      .catch(() => setLeads([]));
  }, [filter, refreshToken, campaignId]);

  async function transition(id: string, action: 'qualify' | 'discard' | 'revert') {
    await apiClient.post(`/leads/${id}/${action}`, {});
    setRefreshToken((x) => x + 1);
  }

  function openImportModal() {
    setImportOpen(true);
    setImportStep('file');
    setCsvFile(null);
    setCsvHeaders([]);
    setColumnMapping({});
    setImportResult(null);
  }

  function closeImportModal() {
    setImportOpen(false);
  }

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      const headers = parseCsvHeaders(text);
      setCsvHeaders(headers);
      const autoMapping: Record<string, string> = {};
      for (const h of headers) {
        autoMapping[h] = guessField(h);
      }
      setColumnMapping(autoMapping);
      setImportStep('mapping');
    };
    reader.readAsText(file);
  }

  function updateMapping(header: string, field: string) {
    setColumnMapping((prev) => ({ ...prev, [header]: field }));
  }

  async function submitImport() {
    if (!csvFile) return;
    setImporting(true);
    setImportErrorDetail(null);
    try {
      const formData = new FormData();
      formData.append('file', csvFile);
      formData.append('mapping', JSON.stringify(columnMapping));
      const { data } = await apiClient.post<CsvImportResult>('/leads/import-csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(data);
      setImportStep('done');
      setRefreshToken((x) => x + 1);
    } catch (err) {
      const detail = isAxiosError(err)
        ? (err.response?.data as { detail?: string } | undefined)?.detail
        : null;
      setImportErrorDetail(detail ?? null);
      setImportResult({ created: 0, duplicates: 0, errors: -1 });
      setImportStep('done');
    } finally {
      setImporting(false);
    }
  }

  const filters: Array<typeof filter> = ['NEW', 'QUALIFIED', 'DISCARDED', 'ALL'];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{t('nav.leads')}</h1>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => setNewLeadOpen(true)}>
            {t('leads.new_lead')}
          </Button>
          <Button size="sm" variant="outline" onClick={openImportModal}>
            {t('leads.import_csv')}
          </Button>
        </div>
      </div>
      <CampaignFilterPill />
      <div className="flex gap-2">
        {filters.map((f) => (
          <Button
            key={f}
            variant={filter === f ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(f)}
          >
            {f}
          </Button>
        ))}
      </div>
      <ul className="divide-y rounded-lg border bg-card">
        {leads.map((l) => (
          <li
            key={l.id}
            onClick={() => navigate(`/leads/${l.id}`)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') navigate(`/leads/${l.id}`);
            }}
            role="button"
            tabIndex={0}
            className="flex cursor-pointer items-center justify-between px-4 py-3 transition-colors hover:bg-muted/50 focus:bg-muted/50 focus:outline-none"
          >
            <div>
              <div className="font-medium">
                {[l.firstName, l.lastName].filter(Boolean).join(' ') || l.email || '—'}
              </div>
              <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                <span>
                  {l.companyName ?? '—'} · {l.source}
                </span>
                {l.campaignName && (
                  <span className="inline-flex max-w-[16rem] items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 font-medium text-primary">
                    <Megaphone className="h-3 w-3 shrink-0" />
                    <span className="truncate">{l.campaignName}</span>
                  </span>
                )}
              </div>
            </div>
            <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
              {l.status === 'NEW' && (
                <>
                  <Button size="sm" onClick={() => transition(l.id, 'qualify')}>
                    Qualify
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => transition(l.id, 'discard')}
                  >
                    Discard
                  </Button>
                </>
              )}
              {(l.status === 'QUALIFIED' || l.status === 'DISCARDED') && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => transition(l.id, 'revert')}
                >
                  Revert to NEW
                </Button>
              )}
            </div>
          </li>
        ))}
        {leads.length === 0 && (
          <li className="px-4 py-6 text-center text-sm text-muted-foreground">—</li>
        )}
      </ul>

      <NewLeadModal
        open={newLeadOpen}
        onClose={() => setNewLeadOpen(false)}
        onCreated={() => setRefreshToken((x) => x + 1)}
      />

      {/* CSV Import Modal */}
      {importOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-lg rounded-lg border bg-background p-6 shadow-lg">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Import Leads from CSV</h2>
              <button
                type="button"
                onClick={closeImportModal}
                className="text-muted-foreground hover:text-foreground"
              >
                X
              </button>
            </div>

            {importStep === 'file' && (
              <div className="mt-4 space-y-4">
                <p className="text-sm text-muted-foreground">
                  Select a CSV file. The first row must contain column headers.
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="block w-full text-sm file:mr-4 file:rounded-md file:border-0 file:bg-primary file:px-4 file:py-2 file:text-sm file:font-medium file:text-primary-foreground hover:file:bg-primary/90"
                />
              </div>
            )}

            {importStep === 'mapping' && (
              <div className="mt-4 space-y-4">
                <p className="text-sm text-muted-foreground">
                  Map each CSV column to a lead field. Unmatched columns will be skipped.
                </p>
                <div className="max-h-64 space-y-2 overflow-auto">
                  {csvHeaders.map((header) => (
                    <div key={header} className="flex items-center gap-3">
                      <span className="w-40 truncate text-sm font-medium">{header}</span>
                      <select
                        className="flex-1 rounded-md border bg-background px-2 py-1.5 text-sm"
                        value={columnMapping[header] ?? ''}
                        onChange={(e) => updateMapping(header, e.target.value)}
                      >
                        {LEAD_FIELDS.map((f) => (
                          <option key={f.value} value={f.value}>
                            {f.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setImportStep('file')}
                  >
                    Back
                  </Button>
                  <Button size="sm" onClick={submitImport} disabled={importing}>
                    {importing ? 'Importing...' : 'Import'}
                  </Button>
                </div>
              </div>
            )}

            {importStep === 'done' && importResult && (
              <div className="mt-4 space-y-4">
                {importResult.errors === -1 ? (
                  <p className="text-sm text-destructive">
                    {importErrorDetail ??
                      'Import failed. Please check the file format and try again.'}
                  </p>
                ) : (
                  <div className="space-y-1 text-sm">
                    <p>
                      Created: <span className="font-semibold">{importResult.created}</span>
                    </p>
                    <p>
                      Duplicates skipped:{' '}
                      <span className="font-semibold">{importResult.duplicates}</span>
                    </p>
                    <p>
                      Errors: <span className="font-semibold">{importResult.errors}</span>
                    </p>
                  </div>
                )}
                <div className="flex justify-end">
                  <Button size="sm" onClick={closeImportModal}>
                    Close
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
